/**
 * =========================================================================================
 *     Copyright © 2025 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   useWorkflowStatus.ts
 *     Description     :   React Query hooks for workflow status endpoints
 *     Author          :   Peter Dobler
 *     Created         :   2026-02-16
 *     Modified        :   2026-02-16 | Peter Dobler | Initial implementation
 *     Constitution    :   v1.3.0 (Enterprise Dependency Architecture)
 *     Feature         :   135-ui-data-alignment
 * =========================================================================================
 */

import { useQuery } from '@tanstack/react-query';
import type {
  WorkflowOnboardingResponse,
  WorkflowSummaryResponse,
  CollectionSyncStatus,
  EntityWorkflowStatus,
} from '../../../shared/types/workflow';
import {
  fetchLakehouseOnboarding,
  fetchWarehouseOnboarding,
  fetchCollectionSyncStatus,
  fetchWorkflowSummary,
  fetchEntityWorkflowStatus,
  type WorkflowOnboardingFilters,
} from '../api/workflowApi';

// ─── Query Keys ──────────────────────────────────────────────────────────────

export const workflowKeys = {
  all: ['workflows'] as const,
  summary: () => [...workflowKeys.all, 'summary'] as const,
  lakehouseOnboarding: (filters: WorkflowOnboardingFilters) =>
    [...workflowKeys.all, 'lakehouse-onboarding', filters] as const,
  warehouseOnboarding: (filters: WorkflowOnboardingFilters) =>
    [...workflowKeys.all, 'warehouse-onboarding', filters] as const,
  collectionSync: () => [...workflowKeys.all, 'collection-sync'] as const,
  entity: (entityType: string, entityId: string) =>
    [...workflowKeys.all, 'entity', entityType, entityId] as const,
};

// ─── Hooks ───────────────────────────────────────────────────────────────────

/**
 * Fetch aggregated workflow summary across all workflow types.
 */
export function useWorkflowSummary() {
  return useQuery<WorkflowSummaryResponse>({
    queryKey: workflowKeys.summary(),
    queryFn: fetchWorkflowSummary,
    staleTime: 120000,
  });
}

/**
 * Fetch lakehouse onboarding workflow status with optional filters.
 */
export function useLakehouseOnboarding(filters: WorkflowOnboardingFilters = {}) {
  return useQuery<WorkflowOnboardingResponse>({
    queryKey: workflowKeys.lakehouseOnboarding(filters),
    queryFn: () => fetchLakehouseOnboarding(filters),
    staleTime: 120000,
  });
}

/**
 * Fetch warehouse onboarding workflow status with optional filters.
 */
export function useWarehouseOnboarding(filters: WorkflowOnboardingFilters = {}) {
  return useQuery<WorkflowOnboardingResponse>({
    queryKey: workflowKeys.warehouseOnboarding(filters),
    queryFn: () => fetchWarehouseOnboarding(filters),
    staleTime: 120000,
  });
}

/**
 * Fetch collection job sync status.
 */
export function useCollectionSyncStatus() {
  return useQuery<CollectionSyncStatus>({
    queryKey: workflowKeys.collectionSync(),
    queryFn: fetchCollectionSyncStatus,
    staleTime: 120000,
  });
}

/**
 * Fetch single-entity workflow status for detail page banners.
 * Returns null if entity not found (404).
 */
export function useEntityWorkflowStatus(
  entityType: string,
  entityId: string,
  enabled = true
) {
  return useQuery<EntityWorkflowStatus | null>({
    queryKey: workflowKeys.entity(entityType, entityId),
    queryFn: () => fetchEntityWorkflowStatus(entityType, entityId),
    staleTime: 120000,
    enabled: enabled && !!entityType && !!entityId,
  });
}
